import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { Lang } from '../i18n';
import { safeLocalStorage } from '../utils/safeStorage';

/**
 * Hook to detect the user's language
 * Priority:
 * 1. URL query parameter (?lang=fr or ?lang=en) - saves to localStorage
 * 2. Check localStorage for previously saved language
 * 3. Default to French ('fr')
 */
export const useBrowserLanguage = (): Lang => {
  const [searchParams] = useSearchParams();

  const supportedLanguages: Lang[] = ['fr', 'en'];

  // Check URL query parameter first
  const urlLang = searchParams.get('lang') || searchParams.get('language');
  let lang: Lang = 'fr';
  
  if (urlLang && supportedLanguages.includes(urlLang as Lang)) {
    lang = urlLang as Lang;
    // Save to localStorage for future sessions
    safeLocalStorage.setItem('preferredLanguage', lang);
  } else {
    // Check localStorage for previously saved language
    const savedLang = safeLocalStorage.getItem('preferredLanguage');
    if (savedLang && supportedLanguages.includes(savedLang as Lang)) {
      lang = savedLang as Lang;
    }
  }

  // Update HTML lang attribute
  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  return lang;
};
